/**
 * Staking calculations using BigDecimal for precision
 *
 * These functions wrap SolomonStake calculation methods, converting between
 * BigDecimal (used in our app) and bigint (used by SolomonStake).
 */

import { BigDecimal } from "@solomon-labs/bigdecimal";
import { SolomonStake } from "@solomon-labs/solomon-protocol";

/**
 * Calculate the unvested amount based on linear vesting schedule
 *
 * Wraps SolomonStake.calculateUnvestedAmount() with BigDecimal conversion.
 *
 * @param vestingAmount - Total amount being vested
 * @param lastDistributionTime - Unix timestamp of last distribution (seconds)
 * @param vestingPeriod - Total vesting duration in seconds
 * @returns The amount that is still unvested
 */
export function getUnvestedAmount(
    vestingAmount: BigDecimal,
    lastDistributionTime: number,
    vestingPeriod: number
): BigDecimal {
    const result = SolomonStake.calculateUnvestedAmount(vestingAmount.toBigint(), lastDistributionTime, vestingPeriod);
    return new BigDecimal(result);
}

/**
 * Convert asset amount (USDv) to shares (sUSDv) for staking
 *
 * Wraps SolomonStake.convertToShares() with BigDecimal conversion.
 * Formula: shares = (assetAmount * totalSupply) / (totalAssets - unvestedAmount)
 *
 * @param assetAmount - Amount of USDv to stake
 * @param totalAssets - Total USDv in the vault
 * @param unvestedAmount - Amount still vesting (from getUnvestedAmount)
 * @param totalSupply - Total sUSDv supply
 * @returns Amount of sUSDv shares to receive
 */
export function convertToShares(
    assetAmount: BigDecimal,
    totalAssets: BigDecimal,
    unvestedAmount: BigDecimal,
    totalSupply: BigDecimal
): BigDecimal {
    const result = SolomonStake.convertToShares(
        assetAmount.toBigint(),
        totalAssets.toBigint(),
        unvestedAmount.toBigint(),
        totalSupply.toBigint()
    );
    return new BigDecimal(result);
}

/**
 * Convert shares amount (sUSDv) to assets (USDv) for unstaking
 *
 * Wraps SolomonStake.convertToAssets() with BigDecimal conversion.
 * Formula: assets = (sharesAmount * (totalAssets - unvestedAmount)) / totalSupply
 *
 * @param sharesAmount - Amount of sUSDv to unstake
 * @param totalAssets - Total USDv in the vault
 * @param unvestedAmount - Amount still vesting (from getUnvestedAmount)
 * @param totalSupply - Total sUSDv supply
 * @returns Amount of USDv assets to receive
 */
export function convertToAssets(
    sharesAmount: BigDecimal,
    totalAssets: BigDecimal,
    unvestedAmount: BigDecimal,
    totalSupply: BigDecimal
): BigDecimal {
    const result = SolomonStake.convertToAssets(
        sharesAmount.toBigint(),
        totalAssets.toBigint(),
        unvestedAmount.toBigint(),
        totalSupply.toBigint()
    );
    return new BigDecimal(result);
}

export { toRawAmount, fromRawAmount } from "@solomon-apps/shared";

/**
 * Calculate the share price (USDv per sUSDv)
 *
 * @param totalAssets - Total USDv in the vault
 * @param unvestedAmount - Amount still vesting
 * @param totalSupply - Total sUSDv supply
 * @returns Price of 1 sUSDv in USDv
 */
export function calculateSharePrice(
    totalAssets: BigDecimal,
    unvestedAmount: BigDecimal,
    totalSupply: BigDecimal
): BigDecimal {
    if (totalSupply.toNumber() === 0) {
        return new BigDecimal(1);
    }

    const totalAssetsLessVested = totalAssets.subtract(unvestedAmount);
    return totalAssetsLessVested.divide(totalSupply);
}
